/**
 * TheoremPanel — the structural results RD-31 establishes.
 *
 * Honest-agent isolation, violation confinement, bounded violation surface,
 * k-resilience, and the BFT (n > 3f) comparison. Each result carries its
 * mechanization status: AMR-1/2/4 are machine-checked in Agda (--safe, zero
 * postulates), AMR-3's finite-universe bound is proved with the tight
 * closed form open, and AMR-5 is the proved CSK side of the BFT comparison.
 * The panel reads its content from data/theorems.json.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import theoremsData from "../data/theorems.json" with { type: "json" };
const TONE_COLOR: Record<string, string> = {
  grounded: panelTokens.honest,
  partial: panelTokens.accentTeal,
  conjectured: panelTokens.surfaceBound,
  analysis: panelTokens.accentBlue,
};
export function TheoremPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="The Theorems"
      subtitle="What the game's rules encode: isolation, confinement, a bounded surface, k-resilience, and why CSK's structural validation beats BFT's n > 3f."
      accentColor={panelTokens.accentBlue}
    >
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          RD-31 extends RD-05's compositional safety into the adversarial
          setting. Where RD-05 gives a binary answer (all honest ⇒ safe; any
          dishonest ⇒ no guarantee), RD-31 gives a graded one: with at most k of
          n agents adversarial, the honest sub-system stays safe and the
          violation surface is confined and bounded.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Results</PanelSurface.SectionHeading>
      <div className="grid grid-cols-1 gap-3">
        {theoremsData.theorems.map((theorem) => {
          const color = TONE_COLOR[theorem.tone] ?? panelTokens.accent;
          return (
            <div
              key={theorem.id}
              className="rounded-[12px] border border-[var(--adv-border)] bg-[rgba(10,6,8,0.5)] px-4 py-3.5"
            >
              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[13px] font-bold text-[var(--adv-bright)]">
                  {theorem.id}
                </span>
                <span className="text-[13px] font-semibold text-[var(--adv-text)]">
                  {theorem.name}
                </span>
                {PanelSurface.badge(theorem.status, color)}
                <span className="ml-auto font-mono text-[11px] text-[var(--adv-muted)]">
                  {theorem.rdSection}
                </span>
              </div>
              <div className="mb-2 text-[12.5px] leading-[1.6] text-[var(--adv-text)]">
                {theorem.statement}
              </div>
              <div className="text-[11.5px] leading-[1.55] text-[var(--adv-muted)]">
                {theorem.basis}
              </div>
            </div>
          );
        })}
      </div>

      <PanelSurface.SectionHeading>The bound, in one block</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>{`-- RD-31 §4.4 violation-surface bound
-- Agda proves the finite-universe bound + honest-surface-zero
-- (ViolationSurfaceBound, honest-violation-surface-zero, --safe).
-- The tight closed form below is the game's estimate (not yet in Agda):
|CLOSE(⋃ ALLOWᵢ) ∩ FORBID|  ≤  k·a³  +  k²·a²·A

   k  = number of compromised agents
   a  = max adversary action space
   A  = total honest action space

-- first term:  single-adversary compositions
-- second term: cross-adversary compositions mediated by honest agents
-- the honest-to-honest square is absent from CLOSE — never counted`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        BFT comparison · why structural beats consensus
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.FactList
          ariaLabel="BFT versus CSK comparison"
          items={[
            {
              id: "bft",
              label: "Byzantine fault tolerance",
              value:
                "Tolerates f Byzantine nodes only if n > 3f. Safety is a quorum property established by consensus over honest majorities.",
            },
            {
              id: "csk",
              label: "CSK k-resilience",
              value:
                "Needs no 3f condition. Governed-interface validation is structural (type-level), not consensus-based. The adversary cannot forge type inhabitants, so the honest core stays safe by construction for any k < n.",
            },
            {
              id: "why",
              label: "Why CSK is stronger",
              value:
                "BFT can fail once f exceeds the quorum threshold. CSK's honest-to-honest violation is inexpressible at every k — there is no threshold past which the honest core becomes constructably breachable.",
            },
          ]}
        />
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
